<script setup lang="ts">
import {onUnmounted, ref, watch} from "vue";
import {GridApi} from "ag-grid-enterprise";
import {Dialog} from "quasar";
import Translator from "@/utils/common/Translator.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import EditScreenGridHandler from "@/utils/common/EditScreenGridHandler.ts";
import DialogHandler, {CommonDialogAction} from "@/utils/forms/DialogHandler.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import {useMessagesStore} from "@/stores/ToastDataStore.ts";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import NamaFindBar from "@/components/EditView/NamaFindBar.vue";
import ControlAgSortColumnsDialog from "@/components/dialogs/ControlAGSortColumnsDialog.vue";
import {EditScreenGridToolbarFunction} from "@/utils/UIConstants/enums.ts";
import {ToolbarAction} from "@/utils/presenters/interfaces.ts";
import EditViewToolbarUtils from "@/utils/editView/EditViewToolbarUtils.ts";
import BusinessFunction from "@/utils/constants/BusinessFunction.ts";
import QuestionField = namaTypes.QuestionField;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;

const props = defineProps<{
  gridApi: GridApi | undefined;
  handler: EditScreenGridHandler;
}>();

const messagesStore = useMessagesStore();
const loadingStore = useLoadingStore();
const canDragRow = ref(!AGGridUtils.getSuppressRowDragFromStorage(props.handler.gridId));
const suppressColumnDrag = ref(AGGridUtils.getSuppressColumnDragFromStorage(props.handler.gridId));

const findBarRef = ref<InstanceType<typeof NamaFindBar>>();

const isAnyFilterPresent = ref(false);

function updateFilterPresence() {
  isAnyFilterPresent.value = !!props.gridApi && !props.gridApi.isDestroyed() && props.gridApi.isAnyFilterPresent();
}

watch(() => props.gridApi, (newApi, oldApi) => {
  if (oldApi && !oldApi.isDestroyed())
    oldApi.removeEventListener('filterChanged', updateFilterPresence);
  newApi?.addEventListener('filterChanged', updateFilterPresence);
  updateFilterPresence();
}, {immediate: true});

onUnmounted(() => {
  if (props.gridApi && !props.gridApi.isDestroyed())
    props.gridApi.removeEventListener('filterChanged', updateFilterPresence);
});

const toolbarActions: ToolbarAction[] = [
  {
    defaultIcon: "manage_search",
    businessFunc: EditScreenGridToolbarFunction.findTextLocally,
    onClick: async () => findBarRef.value?.toggleFindBar(),
    checkIfEnabled: (a) => true,
    checkIfVisible: (a) => !findBarRef.value?.showFindBar
  },
  {
    defaultIcon: "nm-add-line",
    businessFunc: EditScreenGridToolbarFunction.AddNewLine,
    onClick: async () => await AGGridUtils.appendNewLine(props.handler),
    checkIfEnabled: (a) => AGGridUtils.canAppendRow(props.handler.presenter, props.handler.gridId) && !isAnyFilterPresent.value,
  },
  {
    defaultIcon: "nm-duplicate",
    businessFunc: EditScreenGridToolbarFunction.CopyLine,
    onClick: async () => await copyRow(),
    checkIfEnabled: (a) => AGGridUtils.canAppendRow(props.handler.presenter, props.handler.gridId)
      && AGGridUtils.canCopyRow(props.handler.dispatcher, props.handler.gridId),
  },
  {
    defaultIcon: "nm-multi-copy",
    businessFunc: EditScreenGridToolbarFunction.MultiCopy,
    onClick: async () => await duplicateMultipleRowsDialog(),
    checkIfEnabled: (a) => AGGridUtils.canAppendRow(props.handler.presenter, props.handler.gridId)
      && AGGridUtils.canCopyRow(props.handler.dispatcher, props.handler.gridId),
  },
  {
    defaultIcon: "nm-delete",
    class: "active-red hover:tw-bg-red-600/5",
    businessFunc: EditScreenGridToolbarFunction.DeleteLine,
    onClick: async () => await deleteCurrentLine(),
    checkIfEnabled: (a) => AGGridUtils.canDeleteRow(props.handler)
  },
  {
    defaultIcon: "directions_run",
    businessFunc: EditScreenGridToolbarFunction.GoToLine,
    onClick: async () => await goToLine(),
    checkIfEnabled: (a) => true
  },
  {
    defaultIcon: "nm-auto-fit-col",
    businessFunc: EditScreenGridToolbarFunction.AutoFitCols,
    onClick: async () => {
      props.gridApi?.sizeColumnsToFit();
    },
    checkIfEnabled: (a) => true
  },
  {
    defaultIcon: "nm-auto-fit-col-to-content",
    businessFunc: EditScreenGridToolbarFunction.AutoFitColumns,
    onClick: async () => {
      props.gridApi?.autoSizeAllColumns();
    },
    checkIfEnabled: (a) => true
  },
  {
    defaultIcon: "nm-drag-row-indicator",
    businessFunc: EditScreenGridToolbarFunction.RowDrag,
    onClick: async () => {
      AGGridUtils.toggleRowDragWithCallback(props.gridApi!, props.handler.gridId, (value) => {
        canDragRow.value = value;
      });
    },
    checkIfEnabled: (a) => true
  },
  {
    defaultIcon: "nm-drag",
    businessFunc: EditScreenGridToolbarFunction.ColumnsDrag,
    onClick: async () => {
      AGGridUtils.toggleColumnDragWithCallback(props.gridApi!, props.handler.gridId, (value) => {
        suppressColumnDrag.value = value;
      });
    },
    checkIfEnabled: (a) => true
  },
  {
    defaultIcon: "nm-sort",
    businessFunc: EditScreenGridToolbarFunction.ViewSortedColumns,
    onClick: async () => await showSortedColumnsDialog(),
    checkIfEnabled: (a) => true
  },
  {
    defaultIcon: "nm-remove-filter",
    businessFunc: EditScreenGridToolbarFunction.removeFilter,
    class:'active-red',
    checkIfEnabled: (a) => isAnyFilterPresent.value,
    onClick: async () => {
      if (props.gridApi)
        props.gridApi.setFilterModel(null);
    }
  }
];

async function showSortedColumnsDialog() {
  const api = props.gridApi;
  if (!api)
    return;
  const columnState = api.getColumnState();
  const sortedColumns = columnState.filter(col => col.sort)
    .sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0));

  if (sortedColumns.length === 0) {
    messagesStore.pushMessageStr("No columns are currently sorted");
    return;
  }
  Dialog.create({
    component: ControlAgSortColumnsDialog,
    componentProps: {
      gridApi: api
    }
  });
}

async function duplicateMultipleRowsDialog() {
  const api = props.gridApi;
  if (!api) return;
  let selectedRows = AGGridUtils.selectedRowsPassingFilter(api);
  if (!selectedRows.length) {
    let focusedRowData = AGGridUtils.fetchFocusedRowData(api);
    if (!focusedRowData) return;
    selectedRows.push(focusedRowData);
  }
  let okBtn: CommonDialogAction = CommonDialogAction.createOkDialogAction(async (record: Record<string, any>) => {
    let count = record['count'];
    if (selectedRows.length > 1) loadingStore.startLoading();
    for (let i = 0; i < selectedRows.length; i++) {
      let selectedRowData = selectedRows[i];
      if (AGGridUtils.isEmptyLine(selectedRowData)) continue;
      let rows: any[] = NamaCommon.getFieldValue(props.handler.presenter.fetchRecord(), props.handler.gridId);
      let realRowIndex = ObjectChecker.findIndexOf(rows, l => l.id === selectedRowData.id)!;
      await AGGridUtils.asyncCreateRows(selectedRowData, api, realRowIndex + 1, true, props.handler.presenter, props.handler.gridId, count);
      await AGGridUtils.afterRowInserted(props.handler, realRowIndex + 1, true, api);
    }
    if (selectedRows.length > 1) loadingStore.stopLoading();
  });
  let cancelBtn: CommonDialogAction = CommonDialogAction.createCancelDialogAction(() => {
  });
  let questionField: QuestionField = NamaCommon.createQuestionField("count", FieldType.Integer);
  let defaultValue: DTOFilledQuestionField = {id: 'count', value: '1', objValue: 1};
  let dialogBox = new DialogHandler([okBtn, cancelBtn], Translator.translateId("count"), true);
  await dialogBox.createPresenter(props.handler.presenter, [questionField], [defaultValue]);
  await dialogBox.runDialogBox();
}

async function goToLine() {
  const api = props.gridApi;
  if (!api) return;
  let okBtn: CommonDialogAction = CommonDialogAction.createOkDialogAction(async (record: Record<string, any>) => {
    let lineNumber = record['lineNumber'];
    if (api.getDisplayedRowCount() < lineNumber) {
      messagesStore.pushMessageStr("Displayed line Count is less than targeted line number");
      return;
    }
    if (lineNumber <= 0) {
      messagesStore.pushMessageStr("Targeted line number could not be less than or equal to zero");
      return;
    }
    api.ensureIndexVisible(lineNumber - 1, "top");
  });
  let cancelBtn: CommonDialogAction = CommonDialogAction.createCancelDialogAction(() => {
  });
  let questionField: QuestionField = NamaCommon.createQuestionField("lineNumber", FieldType.Integer);
  let defaultValue: DTOFilledQuestionField = {id: 'lineNumber', value: '1', objValue: 1};
  let dialogBox = new DialogHandler([okBtn, cancelBtn], Translator.translateId("goToLine"), true);
  await dialogBox.createPresenter(props.handler.presenter, [questionField], [defaultValue]);
  await dialogBox.runDialogBox();
}

async function copyRow() {
  const api = props.gridApi;
  if (!api) return;
  let selectedRows = AGGridUtils.selectedRowsPassingFilter(api);
  if (!selectedRows.length) {
    let focusedRowData = AGGridUtils.fetchFocusedRowData(api);
    if (!focusedRowData) return;
    selectedRows.push(focusedRowData);
  }
  if (selectedRows.length > 1) loadingStore.startLoading();
  for (let i = 0; i < selectedRows.length; i++) {
    let selectedRowData = selectedRows[i];
    if (AGGridUtils.isEmptyLine(selectedRowData)) {
      messagesStore.pushMessageStr("Could not duplicate empty line");
      return;
    }
    let rows: any[] = NamaCommon.getFieldValue(props.handler.presenter.fetchRecord(), props.handler.gridId);
    let realRowIndex = ObjectChecker.findIndexOf(rows, l => l.id === selectedRowData.id)!;
    await AGGridUtils.asyncCreateRows(selectedRowData, api, realRowIndex + 1, true, props.handler.presenter, props.handler.gridId, 1);
    await AGGridUtils.afterRowInserted(props.handler, realRowIndex + 1, true, api);
  }
  if (selectedRows.length > 1) loadingStore.stopLoading();
}

async function deleteCurrentLine() {
  const api = props.gridApi;
  if (!api) return;
  let selectedRows = AGGridUtils.selectedRowsPassingFilter(api);
  if (!selectedRows.length) {
    let focusedRowData = AGGridUtils.fetchFocusedRowData(api);
    if (!focusedRowData) return;
    selectedRows.push(focusedRowData);
  }
  if (selectedRows.length > 1) loadingStore.startLoading();

  for (let i = 0; i < selectedRows.length; i++) {
    let selectedRowData = selectedRows[i];
    await AGGridUtils.removeLine(selectedRowData, api, props.handler);
  }
  if (selectedRows.length > 1) loadingStore.stopLoading();
}

function toggleFindBar() {
  findBarRef.value?.toggleFindBar();
}

function isInactiveAction(action: ToolbarAction) {
  if (action.businessFunc === EditScreenGridToolbarFunction.RowDrag && !canDragRow.value)
    return true;
  if (action.businessFunc === EditScreenGridToolbarFunction.ColumnsDrag && suppressColumnDrag.value)
    return true;
  if (action.businessFunc === EditScreenGridToolbarFunction.removeFilter && !isAnyFilterPresent.value)
    return true;
  return false;
}

defineExpose({toggleFindBar});
</script>

<template>
  <div class="tw-inline-flex tw-gap-2 tw-items-center tw-px-2 tw-py-[1px] tw-w-max" title="">
    <NamaFindBar ref="findBarRef" :grid-api="gridApi"/>
    <template v-for="action in toolbarActions" :key="action.businessFunc">
      <a
        v-if="EditViewToolbarUtils.isActionVisible(action)"
        @click.stop="action.onClick"
        :title="Translator.translateId(action.businessFunc)"
        :class="[
          {'nama-toolbar-disabled': EditViewToolbarUtils.isActionDisabled(action)},
          isInactiveAction(action)? 'icon-disabled' : (action.class ? action.class : 'active-primary hover:tw-bg-sky-600/10')
        ]"
        class="tw-border-gray-300 dark:tw-border-gray-600 tw-border hover:tw-bg-gray-200 tw-flex tw-items-center tw-justify-center active:tw-bg-gray-300 dark:hover:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-1.5 tw-rounded-md tw-cursor-pointer tw-shrink-0">
        <NamaIcon :default-icon="action.defaultIcon" class="!tw-h-[17px] tw-w-[17px]"/>
      </a>
    </template>
  </div>
</template>
